import { computed, reactive, readonly } from "vue";
import { grantsPermission } from "@riskops/shared";
import { api, type Item } from "../api";

// 与后端 access.ts 的 hasPermission 保持同一套规则：
// 写权限蕴含对应读权限；系统管理域（settings/user/role/department/permission）下，
// 老角色只给了 settings.write 的仍然视为拥有全部系统管理权限。
// 系统配置的「每个配置项一对读写码」（settings.basic.read / settings.log_types.write …）
// 连老码兼容一起都在 @riskops/shared 的 grantsSettingsPermission 里，这里只转发 grantsPermission。
const SYSTEM_AREAS = new Set(["settings", "user", "role", "department", "permission"]);
const SYSTEM_READ_AREAS = new Set(["user", "role", "department", "permission"]);

export function allows(user: Item | null | undefined, permission: string) {
  const granted = (user?.permissions as string[] | undefined) ?? [];
  if (!permission || granted.includes("*") || granted.includes(permission)) return true;
  // 与后端 access.ts 的 hasPermission 完全一致：操作 / 审核 ⇒ 详情 ⇒ 列表 ⇒ 功能
  if (grantsPermission(granted, permission)) return true;
  const [area, action] = permission.split(".");
  if (area && action === "read" && granted.includes(`${area}.write`)) return true;
  if (area && SYSTEM_AREAS.has(area) && granted.includes("settings.write")) return true;
  if (area && action === "read" && SYSTEM_READ_AREAS.has(area) && granted.includes("settings.read")) return true;
  return false;
}

const state = reactive({
  ready: false,
  user: null as Item | null
});

export function useAuth() {
  async function restore() {
    try { state.user = await api.get<Item>("/me"); }
    catch { state.user = null; }
    finally { state.ready = true; }
    return state.user;
  }
  async function loginWithCode(code: string) {
    const result = await api.post<{ user?: Item; totpRequired?: boolean; challenge?: string }>("/login/code", { code });
    if (result.user) {
      state.user = result.user;
      state.ready = true;
    }
    return result;
  }
  async function login(username: string, password: string) {
    const result = await api.post<{ user?: Item; totpRequired?: boolean; challenge?: string }>("/login", { username, password });
    if (result.user) {
      state.user = result.user;
      state.ready = true;
    }
    return result;
  }
  async function confirmTotp(challenge: string, code: string) {
    const result = await api.post<{ user: Item }>("/login/totp", { challenge, code });
    state.user = result.user;
    state.ready = true;
    return result.user;
  }
  async function logout() {
    await api.post("/logout", {});
    state.user = null;
  }
  // 会话失效（401）时由 api 层回调：清掉本地登录态，交给上层跳登录页。
  function clearSession() {
    state.user = null;
    state.ready = true;
  }
  return {
    state: readonly(state),
    signedIn: computed(() => Boolean(state.user)),
    restore, login, loginWithCode, confirmTotp, logout, clearSession
  };
}
